import { useState } from "react";
import {
  Button,
  Checkbox,
  FormControlLabel,
  Box,
  Typography,
  Slider,
} from "@mui/material";
import ShirtModelService from "../../services/shirts/shirtModelService";
import { ShirtType } from "../../services/shirts/types";
import { Operations } from "../../services/api/api";

import { headerStyle, checkBoxStyle, titleStyle, labelStyle } from "./styles";
import { CustomParam } from "../../types/types";

interface FullFilterHandlerProps {
  setFilterValue: Function;
  filterValue: CustomParam<string | number> | undefined;
  typeOptions: Array<ShirtType>;
}

const FullFilterHandler = ({
  filterValue,
  setFilterValue,
  typeOptions,
}: FullFilterHandlerProps) => {
  const minPriceRange: number = 0;
  const maxPriceRange: number = 10000;
  const minPeriodRange: number = 1950;
  const maxPeriodRange: number = 2025;

  const [shirtTypes, setShirtTypes] = useState<Array<number> | undefined>(
    undefined
  );
  const [signature, setSignature] = useState<Array<boolean> | undefined>(
    undefined
  );
  const [priceRange, setPriceRange] = useState<number[]>([
    minPriceRange,
    maxPriceRange,
  ]);
  const [commercial, setCommercial] = useState<Array<boolean> | undefined>(
    undefined
  );
  const [periodRange, setPeriodRange] = useState<number[]>([
    minPeriodRange,
    maxPeriodRange,
  ]);

  const { getAllTypes } = ShirtModelService();

  const removeElementFromArray = (arr: Array<any>, element: any) => {
    const index = arr.indexOf(element);
    if (index !== -1) {
      arr.splice(index, 1);
    }
    return arr;
  };

  function arrayToString(arr: Array<any>): string {
    return `${arr.join(",")}`;
  }

  const priceRangeText = (value: number) => {
    return `€${value}`;
  };

  const handleChangeShirtTypes = (newStatus: boolean, newValue: number) => {
    let stTemp = shirtTypes ? [...shirtTypes] : [];
    if (newStatus) {
      stTemp.push(newValue);
      setShirtTypes(stTemp);
    } else {
      stTemp = removeElementFromArray(stTemp, newValue);
      setShirtTypes(stTemp);
    }
  };

  const handleChangeSignature = (newStatus: boolean, newValue: boolean) => {
    let stTemp = signature ? [...signature] : [];
    if (newStatus) {
      stTemp.push(newValue);
      setSignature(stTemp);
    } else {
      stTemp = removeElementFromArray(stTemp, newValue);
      setSignature(stTemp);
    }
  };

  const handleChangeCommercial = (newStatus: boolean, newValue: boolean) => {
    let stTemp = commercial ? [...commercial] : [];
    if (newStatus) {
      stTemp.push(newValue);
      setCommercial(stTemp);
    } else {
      stTemp = removeElementFromArray(stTemp, newValue);
      setCommercial(stTemp);
    }
  };

  const handlePriceRangeChange = (
    event: Event,
    newValue: number | number[]
  ) => {
    setPriceRange(newValue as number[]);
  };
  const handlePeriodRangeChange = (
    event: Event,
    newValue: number | number[]
  ) => {
    setPeriodRange(newValue as number[]);
  };

  const setFilter = () => {
    let filter: CustomParam<string | number | number[]> = {};
    if (shirtTypes) {
      filter["type_id"] = {
        value: arrayToString(shirtTypes),
        operation: Operations.IN,
      };
    }

    if (signature) {
      filter["signature"] = {
        value: arrayToString(signature),
        operation: Operations.IN,
      };
    }

    if (priceRange) {
      filter["price[" + Operations.GREATER + "]"] = {
        value: priceRange[0],
        operation: Operations.NONE,
      };
      filter["price[" + Operations.LOWER + "]"] = {
        value: priceRange[1],
        operation: Operations.NONE,
      };
    }

    if (commercial) {
      filter["irresistible_offer_status"] = {
        value: arrayToString(commercial),
        operation: Operations.IN,
      };
    }

    if (periodRange) {
      filter["season[" + Operations.GREATER + "]"] = {
        value: periodRange[0],
        operation: Operations.NONE,
      };
      filter["season[" + Operations.LOWER + "]"] = {
        value: periodRange[1],
        operation: Operations.NONE,
      };
    }

    setFilterValue(filter);
  };

  return (
    <Box
      sx={{
        padding: "50px 25px",
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", rowGap: 2 }}>
        <Box sx={{ display: "flex", flexDirection: "column", rowGap: 1 }}>
          <Box sx={headerStyle}>
            <Typography variant="shirtViewText" sx={titleStyle}>
              Shirt Type
            </Typography>
          </Box>
          <Box sx={{ display: "flex", flexDirection: "row", flexWrap: "wrap" }}>
            {typeOptions.map((type, key) => (
              <Box key={key} sx={{ flex: "0 0 50%" }}>
                <FormControlLabel
                  sx={titleStyle}
                  control={
                    <Checkbox
                      size="small"
                      onChange={(event) =>
                        handleChangeShirtTypes(event.target.checked, type.id)
                      }
                      name={type.name}
                      sx={checkBoxStyle}
                    />
                  }
                  label={type.name}
                />
              </Box>
            ))}
          </Box>
        </Box>
        <Box sx={{ display: "flex", flexDirection: "column", rowGap: 1 }}>
          <Box sx={headerStyle}>
            <Typography variant="shirtViewText" sx={titleStyle}>
              Signature
            </Typography>
          </Box>
          <Box sx={{ display: "flex", flexDirection: "row" }}>
            <Box sx={{ flex: "0 0 50%" }}>
              <FormControlLabel
                sx={titleStyle}
                control={
                  <Checkbox
                    size="small"
                    onChange={(event) =>
                      handleChangeSignature(event.target.checked, true)
                    }
                    name="signature"
                    sx={checkBoxStyle}
                  />
                }
                label="Yes"
              />
            </Box>
            <Box sx={{ flex: "0 0 50%" }}>
              <FormControlLabel
                sx={titleStyle}
                control={
                  <Checkbox
                    size="small"
                    onChange={(event) =>
                      handleChangeSignature(event.target.checked, false)
                    }
                    name="signature"
                    sx={checkBoxStyle}
                  />
                }
                label="No"
              />
            </Box>
          </Box>
        </Box>
        <Box sx={{ display: "flex", flexDirection: "column", rowGap: 1 }}>
          <Box sx={headerStyle}>
            <Typography variant="shirtViewText" sx={titleStyle}>
              Price (€)
            </Typography>
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box sx={{ width: "fit-content" }}>
              <Typography variant="shirtViewText" sx={labelStyle}>
                {"€" + priceRange[0]}
              </Typography>
            </Box>
            <Box
              sx={{
                width: "calc(100% - 150px)",
                display: "flex",
                alignItems: "center",
              }}
            >
              <Slider
                size="small"
                getAriaLabel={() => "Price range"}
                value={priceRange}
                onChange={handlePriceRangeChange}
                valueLabelDisplay="auto"
                getAriaValueText={priceRangeText}
                color="secondary"
                max={maxPriceRange}
              />
            </Box>
            <Box sx={{ width: "fit-content" }}>
              <Typography variant="shirtViewText" sx={labelStyle}>
                {"€" + priceRange[1]}
              </Typography>
            </Box>
          </Box>
        </Box>
        <Box sx={{ display: "flex", flexDirection: "column", rowGap: 1 }}>
          <Box sx={headerStyle}>
            <Typography variant="shirtViewText" sx={titleStyle}>
              Period
            </Typography>
          </Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box>
              <Typography variant="shirtViewText" sx={labelStyle}>
                {periodRange[0]}
              </Typography>
            </Box>
            <Box
              sx={{
                width: "calc(100% - 150px)",
                display: "flex",
                alignItems: "center",
              }}
            >
              <Slider
                size="small"
                getAriaLabel={() => "Period range"}
                value={periodRange}
                onChange={handlePeriodRangeChange}
                valueLabelDisplay="auto"
                color="secondary"
                min={minPeriodRange}
                max={maxPeriodRange}
              />
            </Box>
            <Box>
              <Typography variant="shirtViewText" sx={labelStyle}>
                {periodRange[1]}
              </Typography>
            </Box>
          </Box>
        </Box>
        <Box sx={{ width: "100%" }}>
          <Button fullWidth variant="blue" onClick={setFilter}>
            Confirm
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default FullFilterHandler;
